import { useCallback, useEffect, useRef, useState } from "react";
import { ImageUp, Loader2, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** One square logo becomes the app icon on the store listing, the home screen and the installed app. */
export const StoreIconUpload = ({ testId = "store-icon" }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const input = useRef(null);

  const load = useCallback(async () => {
    try {
      setState((await api.get("/company/store-icon")).data);
    } catch (e) {
      setError(errMsg(e));
      setState({ ready: false, urls: {} });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      setState((await api.post("/company/store-icon", body,
        { headers: { "Content-Type": "multipart/form-data" } })).data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const drop = async () => {
    setBusy(true);
    try {
      setState((await api.delete("/company/store-icon")).data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testId} className="space-y-3">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-gold">App icon</p>
        <p className="mt-2 text-sm text-slate-400">
          A square logo, 1024x1024 or larger. We make every size the App Store, Play Store and your
          passengers' home screens need — including a flattened one, because Apple rejects an icon
          with transparency.
        </p>
      </div>
      {state?.ready && (
        <div className="flex items-center gap-3">
          <img data-testid={`${testId}-preview`} src={state.urls["512"]} alt="App icon"
            className="h-20 w-20 rounded-2xl border border-ink-600 object-cover" />
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <p>1024 · 512 · 192 ready</p>
            <Button data-testid={`${testId}-remove`} variant="ghost" size="sm" disabled={busy}
              onClick={drop} className="mt-1 h-auto p-0 text-slate-400 hover:text-red-400">
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove
            </Button>
          </div>
        </div>
      )}
      <input ref={input} data-testid={`${testId}-input`} type="file"
        accept="image/png,image/jpeg,image/webp" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} />
      <Button data-testid={`${testId}-upload`} variant="outline" disabled={busy}
        onClick={() => input.current?.click()}>
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageUp className="mr-2 h-4 w-4" />}
        {state?.ready ? "Replace icon" : "Upload icon"}
      </Button>
      {error && <p data-testid={`${testId}-error`} className="text-sm text-red-400">{error}</p>}
    </div>
  );
};
